import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { convertDateTimeToUTC, isEmpty } from '../../../common/common';
import { saveScheduleDateTime, saveRawScheduleDateTime } from '../../../store/action/OrderAction';
import { useCallCenterFlag, useCartData, useScheduleDate } from '../../../hooks/order';
import { buildSlotsByDate, parseRawScheduleDateTime, formatLocalDate } from './scheduleUtils';
import ScheduleCalendar from './ScheduleCalendar';

const VISIBLE_DATE_COUNT = 14;

// Figma's desktop appointment step replaces ScheduleInstallation.js's month-view antd
// Calendar with a flat grid of upcoming date buttons - same scheduleAppt data and the same
// saveScheduleDateTime/saveRawScheduleDateTime save actions, just a different control.
const DesktopDateTimePicker = ({ scheduleAppt, isMobileAdded, setActiveKey }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isAfterHours } = useCallCenterFlag();
  const { rawScheduleDateTime } = useScheduleDate();
  const { spanishLanguagePref } = useCartData();

  const slotsByDate = useMemo(() => buildSlotsByDate(scheduleAppt), [scheduleAppt]);
  const dates = Object.keys(slotsByDate);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarAnchorRef = useRef(null);

  // Re-opening this step (via "Edit") re-mounts the component - pre-select whatever was
  // already saved instead of resetting to the first available date/time.
  const { date: savedDate, time: savedTime } = useMemo(
    () => parseRawScheduleDateTime(rawScheduleDateTime),
    [rawScheduleDateTime]
  );

  // A fresh visit pre-selects the first available date and its first time slot
  // (buildSlotsByDate sorts both chronologically). Re-opening this step (via "Edit")
  // pre-selects whatever was already saved instead.
  const initialDate = (savedDate && dates.includes(savedDate)) ? savedDate : (dates[0] || '');
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const timeOptions = useMemo(() => slotsByDate[selectedDate] || [], [slotsByDate, selectedDate]);
  const initialTime = (savedTime && timeOptions.includes(savedTime)) ? savedTime : (timeOptions[0] || '');
  const [selectedTime, setSelectedTime] = useState(initialTime);

  // Sync selection if scheduleAppt loads asynchronously after initial mount
  React.useEffect(() => {
    if ((!selectedDate || !dates.includes(selectedDate)) && dates.length > 0) {
      const nextDate = (savedDate && dates.includes(savedDate)) ? savedDate : dates[0];
      setSelectedDate(nextDate);
      const nextSlots = slotsByDate[nextDate] || [];
      const nextTime = (savedTime && nextSlots.includes(savedTime)) ? savedTime : (nextSlots[0] || '');
      setSelectedTime(nextTime);
    }
  }, [dates, slotsByDate, savedDate, savedTime, selectedDate]);

  // A date picked from the "Other Dates" calendar may fall past the grid - swap it into the
  // last slot so the selection stays visible.
  const visibleDates = useMemo(() => {
    const firstDates = dates.slice(0, VISIBLE_DATE_COUNT);
    if (!selectedDate || firstDates.includes(selectedDate) || !dates.includes(selectedDate)) {return firstDates;}
    return [...firstDates.slice(0, -1), selectedDate];
  }, [dates, selectedDate]);

  // Close the calendar on any click outside it (or its "Other Dates" trigger).
  useEffect(() => {
    if (!isCalendarOpen) {return undefined;}
    const handleOutsideClick = (e) => {
      if (calendarAnchorRef.current && !calendarAnchorRef.current.contains(e.target)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isCalendarOpen]);

  const handleCalendarSelect = (date) => {
    handleDateClick(date);
    setIsCalendarOpen(false);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const availableSlots = slotsByDate[date] || [];
    setSelectedTime(availableSlots[0] || '');
  };

  const handleSave = () => {
    if (isEmpty(selectedDate) || isEmpty(selectedTime)) {return;}
    const combineDate = `${moment(selectedDate).format('YYYY-MM-DD')} to ${selectedTime}`;
    dispatch(saveScheduleDateTime(convertDateTimeToUTC(combineDate)));
    dispatch(saveRawScheduleDateTime(combineDate));
    setActiveKey(isMobileAdded ? '2' : '3');
  };

  const handleSkip = () => setActiveKey(isMobileAdded ? '2' : '3');

  return (
    <div className="spc-appointment-picker">
      <div className="spc-appointment-picker__row">
        <span className="spc-appointment-picker__label">{t('singlePageCheckout.chooseADate')}</span>
        {dates.length > VISIBLE_DATE_COUNT && (
          <div className="spc-desktop-calendar-anchor" ref={calendarAnchorRef}>
            <button type="button" className="spc-link-button" onClick={() => setIsCalendarOpen((prev) => !prev)}>
              {t('singlePageCheckout.otherDates')}
            </button>
            {isCalendarOpen && (
              <ScheduleCalendar
                dates={dates}
                slotsByDate={slotsByDate}
                selectedDate={selectedDate}
                onSelect={handleCalendarSelect}
                spanishLanguagePref={spanishLanguagePref}
              />
            )}
          </div>
        )}
      </div>

      <div className="spc-date-grid">
        {visibleDates.map((date) => (
          <button
            type="button"
            key={date}
            className={`spc-date-button ${date === selectedDate ? 'is-selected' : ''}`}
            onClick={() => handleDateClick(date)}
          >
            <span className="spc-date-button__day">{formatLocalDate(date, 'ddd', spanishLanguagePref)}</span>
            <span className="spc-date-button__date">{formatLocalDate(date, 'MMM D', spanishLanguagePref)}</span>
          </button>
        ))}
      </div>

      <div className="spc-appointment-picker__row">
        <span className="spc-appointment-picker__label">{t('singlePageCheckout.chooseATime')}</span>
      </div>

      <div className="spc-time-grid">
        {timeOptions.map((slot) => (
          <button
            type="button"
            key={slot}
            className={`spc-time-button ${slot === selectedTime ? 'is-selected' : ''}`}
            onClick={() => setSelectedTime(slot)}
          >
            {slot.replace(/\s+to\s+/gi, ' – ')}
          </button>
        ))}
      </div>

      <div className="spc-appointment-picker__actions">
        <button type="button" className="blue-button spc-select-button" onClick={handleSave} disabled={isEmpty(selectedDate) || isEmpty(selectedTime)}>
          {t('singlePageCheckout.saveDateTime')}
        </button>
        {!isAfterHours && (
          <button type="button" className="spc-link-button" onClick={handleSkip}>{t('common.skip')}</button>
        )}
      </div>
    </div>
  );
};

export default DesktopDateTimePicker;
